import React, { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from "react-i18next";
import { FilterChoice, FilterValues, GlobalFilterValues } from "../Listing";
import { Dropdown } from "react-bootstrap";
import useTranslationDataUtils from "../../../Vendor/Utils/TranslationDataUtils";
import { DataType } from "../../../Vendor/Components/Form";
import Filter from "../../../Entity/Collection/ListingSetting/Filter";
import Collection, { getShortcut } from "../../../Config/Collection";
import Choice from "../../../Entity/Property/Choice";

export type RangeType = 'date' | 'number'

export interface _ChoiceFilterProps {
    entity?: Collection
    id?: number
    filter: Filter
    choices: FilterChoice[] | undefined
    optionsRefreshToken?: string | number | boolean
    filterValues?: FilterValues
    setFilterValues: (filterValues: FilterValues) => void
    setNewSearch: (criteria: DataType) => void
    setDropdownOpen: (isOpen: boolean) => void
    isOpen: boolean
    globalFilterValues?: GlobalFilterValues
    onOpenAllChoicesModal?: () => void
}

const _ChoiceFilter: FC<_ChoiceFilterProps> = ({ entity, id, filter, choices, optionsRefreshToken, filterValues, setFilterValues, setNewSearch, setDropdownOpen, isOpen, globalFilterValues, onOpenAllChoicesModal }) => {
    const { t } = useTranslation()
    const { translate, translateField } = useTranslationDataUtils()
    const select2Ref = useRef<HTMLSelectElement | null>(null)
    const [options, setOptions] = useState<number>(0)
    const [totalOptions, setTotalOptions] = useState<number>(0)
    const [hasManyChoices, setHasManyChoices] = useState<boolean>(() => {
        return choices ? choices.length >= 10 : false
    })
    const hasManyChoicesRef = useRef<boolean>(choices ? choices.length >= 10 : false)
    const isInitialized = useRef<boolean>(false)
    const currentFilterValues = useRef<any>(filterValues)
    const latestGlobalFilterValues = useRef<GlobalFilterValues | undefined>(globalFilterValues)

    useEffect(() => {
        if (choices !== undefined) {
            const hasMany = choices.length >= 10
            setHasManyChoices(hasMany)
            hasManyChoicesRef.current = hasMany
        }
    }, [choices])

    useEffect(() => {
        currentFilterValues.current = filterValues
    }, [filterValues])

    useEffect(() => {
        latestGlobalFilterValues.current = globalFilterValues
    }, [globalFilterValues])

    const ensureSelectedOptions = ($select: JQuery<HTMLSelectElement>, values?: FilterValues) => {
        if (!values) {
            return
        }

        Object.entries(values).forEach(([value, label]) => {
            if ($select.find(`option[value="${value}"]`).length === 0) {
                const option = new Option(String(label), value, true, true)
                $select.append(option)
            }
        })
    }

    useEffect(() => {
        if (select2Ref.current && entity && !isInitialized.current) {
            const $select = $(select2Ref.current)
            const apiBasePath = 'https://api.' + window.location.host.split(':')[0] + (process.env.REACT_APP_BACKEND_PORT ? ':' + process.env.REACT_APP_BACKEND_PORT : '')
            const $dropdownParent = $select.closest('.modal.show, .offcanvas.show, .modal, .offcanvas')

            let shortcut = getShortcut(entity)
            if (!shortcut) {
                return;
            }
            let slug = ['crm', 'shopping'].includes(shortcut) ? entity : id;
            let url = `/listing/${shortcut}/${slug}/filter/${filter.slug}/choices`

            const isNotToken = (v: any) => (typeof v === 'string' && v.startsWith('not:'))

            const setMutualExclusionUI = ($sel: JQuery<HTMLElement>, keepNotSelected: boolean) => {
                $sel.find('option').each(function () {
                    const $opt = $(this)
                    const val = $opt.attr('value') || ''
                    if (isNotToken(val)) {
                        $opt.prop('disabled', !keepNotSelected)
                    } else {
                        $opt.prop('disabled', keepNotSelected)
                    }
                })
                $sel.trigger('change.select2')
            }

            const updateExpandButton = () => {
                if (!onOpenAllChoicesModal) {
                    return
                }

                const $container = $('.select2-container--open')
                const $search = $container.find('.select2-search')

                if (!$search.length) {
                    return
                }
                
                if (hasManyChoicesRef.current) {
                    $search.addClass('has-expand-btn')
                    if (!$search.find('.select2-expand-modal-btn').length) {
                        const $btn = $(`
                            <button type="button" class="select2-expand-modal-btn" title="${t('app.see_all', 'Tout afficher')}">
                                <i class="fa-solid fa-list-ul"></i>
                            </button>
                        `)
                        $btn.on('click', (e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (select2Ref.current) {
                                $(select2Ref.current).select2('close')
                            }
                            setDropdownOpen(false)
                            onOpenAllChoicesModal()
                        })
                        $search.append($btn)
                    }
                } else {
                    $search.removeClass('has-expand-btn')
                    $search.find('.select2-expand-modal-btn').remove()
                }
            }

            $select.select2({
                theme: "bootstrap-5",
                dropdownParent: $dropdownParent.length ? $dropdownParent : undefined,
                language: {
                    searching: function () {
                        return t('app.loading')
                    },
                    noResults: function () {
                        return t('app.no_result')
                    }
                },
                closeOnSelect: false,
                multiple: true,
                ajax: {
                    url: apiBasePath + url,
                    type: 'POST',
                    data: function (params: any) {
                        const currentGlobal = latestGlobalFilterValues.current || {};
                        // build a filters object by keeping only non-empty object values and replacing them with their keys
                        const appliedFilters = Object.fromEntries(
                            Object.entries(currentGlobal)
                                .map(([key, value]) => {
                                    if (!value || typeof value !== 'object') return undefined;
                                    const keysOnly = Object.keys(value);

                                    return keysOnly.length ? [key, keysOnly] : undefined;
                                })
                                .filter((entry): entry is [string, string[]] => !!entry)
                        );

                        return JSON.stringify({
                            filters: {
                                ...appliedFilters
                            },
                            term: params.term
                        });
                    },
                    dataType: 'json',
                    delay: 250,
                    xhrFields: {
                        withCredentials: true
                    },
                    processResults: function (data: any, params: any) {
                        let length = 0
                        if (typeof data.choices !== 'undefined') {
                            length = data.choices.length
                        }
                        setOptions(length + 1)
                        let totalChoices = 0
                        if (typeof data.totalChoices !== 'undefined') {
                            totalChoices = data.totalChoices
                        }
                        setTotalOptions(totalChoices + 1)

                        if (!params?.term && Array.isArray(data.choices)) {
                            const hasMany = data.choices.length >= 10
                            setHasManyChoices(hasMany)
                            hasManyChoicesRef.current = hasMany
                            setTimeout(updateExpandButton, 0)
                        }

                        let results: { id: string; text: string }[] = [];

                        if (filter.property?.type !== 'combined') {
                            results.push({
                                id: `not:${filter.property?.name || filter.slug}`,
                                text: t('app.not_attributed', {
                                    property: filter.property
                                        ? translate(filter.property, 'label')
                                        : t(`app.${filter.filters}`)
                                })
                            });
                        }

                        if (typeof data.choices !== 'undefined') {
                            const mappedChoices = data.choices.map((choice: Choice) => ({
                                id: choice.value,
                                text: typeof choice.key === 'string'
                                    ? (choice.key.includes('.') ? t(choice.key) : choice.key)
                                    : (choice.key ? translateField(choice.key, 'label') : choice.value),
                            }));

                            results = [...results, ...mappedChoices];
                        }

                        return { results };

                    },
                }
            })

            if (currentFilterValues.current) {
                ensureSelectedOptions($select, currentFilterValues.current)
                const selectedKeys = Object.keys(currentFilterValues.current)
                $select.val(selectedKeys).trigger('change')
                const hasNot = selectedKeys.some(isNotToken)
                setMutualExclusionUI($select, hasNot)
            }

            $select.on('select2:select', (event) => {
                const selectedValue = event.params.data.id
                const selectedLabel = event.params.data.text
                let tempFilterValues = { ...currentFilterValues.current }
                const currentSelected = $select.val() as string[] || []

                if (isNotToken(selectedValue)) {
                    currentSelected.forEach((v) => {
                        if (!isNotToken(v) && v !== selectedValue) {
                            if (tempFilterValues.hasOwnProperty(v)) {
                                delete tempFilterValues[v]
                            }
                        }
                    })

                    tempFilterValues[selectedValue] = selectedLabel
                    $select.val([selectedValue]).trigger('change.select2')
                    setMutualExclusionUI($select, true)
                    setFilterValues(tempFilterValues)
                    return
                }

                Object.keys(tempFilterValues).forEach((k) => {
                    if (isNotToken(k)) {
                        delete tempFilterValues[k]
                    }
                })

                let key = ''

                if (filter.property?.type === 'checkbox') {
                    key += translate(filter.property, 'label') + ': '
                }
                key += selectedLabel
                tempFilterValues[selectedValue] = key

                const newSelected = (currentSelected || []).filter(v => !isNotToken(v))
                if (!newSelected.includes(selectedValue)) {
                    newSelected.push(selectedValue)
                }
                $select.val(newSelected).trigger('change.select2')
                setMutualExclusionUI($select, false)
                setFilterValues(tempFilterValues)
            })

            $select.on('select2:unselect', (event) => {
                const selectedValue = event.params.data.id

                let tempFilterValues = { ...currentFilterValues.current }

                if (tempFilterValues.hasOwnProperty(selectedValue)) {
                    delete tempFilterValues[selectedValue]
                }
                const currentlySelected = $select.val() as string[] || []
                const hasNot = (currentlySelected || []).some(isNotToken)
                setMutualExclusionUI($select, hasNot)
                setFilterValues(tempFilterValues)
            })

            $select.on('select2:open', () => {
                if (onOpenAllChoicesModal) {
                    setTimeout(updateExpandButton, 0)
                }
            })

            $select.on('select2:close', (event) => {
                setDropdownOpen(false)
            })

            isInitialized.current = true

            return () => {
                if ($select.hasClass('select2-hidden-accessible')) {
                    $select.off('select2:select')
                    $select.off('select2:unselect')
                    $select.off('select2:open')
                    $select.off('select2:close')
                    $select.select2('destroy')
                }
                isInitialized.current = false
            }
        }
    }, [entity, id, filter.slug, optionsRefreshToken])

    useEffect(() => {
        if (isOpen && isInitialized.current && select2Ref.current) {
            setTimeout(() => {
                if (select2Ref.current) {
                    const $select = $(select2Ref.current);
                    ensureSelectedOptions($select, filterValues);
                    const selectedKeys = filterValues ? Object.keys(filterValues) : [];
                    $select.val(selectedKeys).trigger('change');
                    $select.select2('open');
                }
            }, 10);
        }

        if (!isOpen && isInitialized.current && select2Ref.current) {
            const $select = $(select2Ref.current);
            if ($select.hasClass('select2-hidden-accessible')) {
                $select.select2('close');
            }
            (document.activeElement as HTMLElement)?.blur();
            $select.val([]).trigger('change');
        }
    }, [isOpen, filterValues]);


    useEffect(() => {
        if (isInitialized.current && select2Ref.current) {
            const $select = $(select2Ref.current)
            ensureSelectedOptions($select, filterValues);
            const currentSelected = $select.val() as string[]
            const newSelected = filterValues ? Object.keys(filterValues) : []

            if (JSON.stringify(currentSelected?.sort()) !== JSON.stringify(newSelected?.sort())) {
                $select.val(newSelected).trigger('change')
            }
        }
    }, [filterValues])

    return <>
        <Dropdown.Item key={0} className="choice-filter">
            <select ref={select2Ref} className="form-select d-flex justify-content-between" multiple></select>
        </Dropdown.Item>
        <Dropdown.Item>
            <div className="d-flex justify-content-between align-items-center">
                {onOpenAllChoicesModal && hasManyChoices ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-decoration-none text-primary"
                        onClick={() => {
                            if (select2Ref.current) {
                                $(select2Ref.current).select2('close')
                            }
                            setDropdownOpen(false)
                            onOpenAllChoicesModal()
                        }}
                    >
                        <i className="fas fa-list-ul me-1"></i>
                        {t('app.see_all', 'Tout afficher')}
                    </button>
                ) : (
                    <p className="mb-0 small text-muted">
                        {totalOptions > options ? t('app.remaining_result', { count: totalOptions - options }) : ''}
                    </p>
                )}
                <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                        if (select2Ref.current) {
                            const $select = $(select2Ref.current);
                            if ($select.hasClass('select2-hidden-accessible')) {
                                $select.select2('close');
                            }
                        }
                        (document.activeElement as HTMLElement)?.blur();
                        setDropdownOpen(false);
                    }}
                >
                    {t('app.close')}
                </button>
            </div>
        </Dropdown.Item>
    </>
}

export default _ChoiceFilter;
